import { APP_BASENAME } from '@/features/properties/lib/property-urls';

/** In-router public auth routes (basename-relative). */
const PUBLIC_AUTH_ROUTES = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/set-password',
  '/mfa/verify',
] as const;

/** Pre-log pages that must bounce an already-signed-in session into the app (DEF-432). */
const PRE_LOG_GUEST_ROUTES = ['/login', '/register', '/forgot-password'] as const;

/**
 * Strip the `/v2` basename so route checks work on both in-router and
 * `window.location.pathname` values.
 */
function barePathname(pathname?: string): string {
  const path =
    pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  if (!path) return '';
  if (path.startsWith(`${APP_BASENAME}/`)) return path.slice(APP_BASENAME.length);
  if (path === APP_BASENAME) return '/';
  return path;
}

/**
 * True when the browser is on a public auth route (login, register, reset, MFA, SSO callback).
 * Uses the full `window.location.pathname` so axios interceptors match `/v2/...` URLs.
 */
export function isPublicAuthPathname(pathname?: string): boolean {
  const bare = barePathname(pathname);
  if (!bare) return false;
  if (bare.startsWith('/auth/')) return true;
  return PUBLIC_AUTH_ROUTES.some(
    (route) => bare === route || bare.startsWith(`${route}/`),
  );
}

/**
 * True when the browser is on the login route (accounts for `/v2` basename).
 */
export function isLoginPathname(pathname?: string): boolean {
  const bare = barePathname(pathname);
  if (!bare) return false;
  return bare === '/login' || bare.startsWith('/login/');
}

/**
 * True when the browser is on login, register, or forgot-password (DEF-432).
 * Authenticated sessions must hydrate here and bounce into the app — unlike
 * recovery / MFA pages, which own their own session handling.
 */
export function isPreLogGuestPathname(pathname?: string): boolean {
  const bare = barePathname(pathname);
  if (!bare) return false;
  return PRE_LOG_GUEST_ROUTES.some(
    (route) => bare === route || bare.startsWith(`${route}/`),
  );
}

/**
 * True when useAuthInit should skip mirroring a live Supabase session.
 * Recovery, MFA, and SSO-callback pages keep their own hydration.
 */
export function shouldSkipSupabaseHydration(pathname?: string): boolean {
  return isPublicAuthPathname(pathname) && !isPreLogGuestPathname(pathname);
}

/**
 * Whether a Supabase SIGNED_OUT event must not wipe the Zustand session.
 *
 * Password login calls `signOut({ scope: 'local' })` to drop leftovers before
 * MFA. That event can land after verify mints a session (FR-356): the MFA
 * page then treats a cleared challenge as "go back to sign in", and the
 * dashboard 401 hard-refreshes to login. Legacy stays logged in after a
 * valid code (`login.js` `validate2FACode` → `setUserLoginDetails`).
 */
export function shouldIgnoreAuthSignedOut(input: {
  hasPendingTwoFactor: boolean;
  isAuthenticated: boolean;
  hasAccessToken: boolean;
  pathname?: string;
}): boolean {
  if (input.hasPendingTwoFactor) return true;
  if (shouldSkipSupabaseHydration(input.pathname)) return true;
  if (input.isAuthenticated && input.hasAccessToken) return true;
  return false;
}
